import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useBackClose } from "@/mobile/backClose";

export interface MenuItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  danger?: boolean;
}

// Right-click menu (tg-gallery §3.7). Opens at the cursor, pulls itself back
// inside the viewport, closes on an outside click, Escape, scroll or resize.
// `sheet` — the phone: a list at the bottom of the screen over a dimmed feed,
// with «Отмена»; it stays put when the keyboard slides away (a resize).
export function ContextMenu({
  x,
  y,
  items,
  onClose,
  sheet = false,
}: {
  x: number;
  y: number;
  items: MenuItem[];
  onClose: () => void;
  sheet?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  useBackClose(onClose, sheet);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || sheet) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      left: Math.min(x, window.innerWidth - width - 8),
      top: Math.min(y, window.innerHeight - height - 8),
    });
  }, [x, y, sheet]);

  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    if (sheet) return () => window.removeEventListener("keydown", onKey);
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, sheet]);

  const list = items.map((item) => (
    <button
      key={item.label}
      type="button"
      className={item.danger ? "ctxmenu__item is-danger" : "ctxmenu__item"}
      role="menuitem"
      onClick={() => {
        item.onClick();
        onClose();
      }}
    >
      <span className="ctxmenu__icon">{item.icon}</span>
      {item.label}
    </button>
  ));

  if (sheet) {
    return (
      <div className="m-sheet-backdrop" onClick={onClose} onContextMenu={(e) => e.preventDefault()}>
        <div className="m-action-sheet" role="menu" onClick={(e) => e.stopPropagation()}>
          {list}
          <button type="button" className="m-action m-action--cancel" onClick={onClose}>
            Отмена
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="ctxmenu"
      style={pos}
      role="menu"
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {list}
    </div>
  );
}

/** Текст в буфер обмена. Clipboard API есть только на https и в жесте
 *  пользователя; где его нет (http-стенд, старый WebView) — через выделение
 *  скрытого поля. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* ниже — запасной путь */
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

export const CopyIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <rect x="8" y="8" width="12" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
  </svg>
);

export const EditIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      d="M4 20h4l10-10-4-4L4 16zM14.5 5.5l4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DeleteIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      d="M5 7h14M9 7V5h6v2m-8 0 .9 12h8.2L17 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CloseIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      d="M6.5 6.5l11 11M17.5 6.5l-11 11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);
